// Validações básicas
const validations = {
  cpf: (value) => {
    if (!value) return null;
    const cpf = value.replace(/\D/g, '');
    if (cpf.length !== 11) return 'CPF deve ter 11 dígitos';
    if (/^(\d)\1{10}$/.test(cpf)) return 'CPF inválido';
    return null;
  },
  
  cnpj: (value) => {
    if (!value) return null;
    const cnpj = value.replace(/\D/g, '');
    if (cnpj.length !== 14) return 'CNPJ deve ter 14 dígitos';
    if (/^(\d)\1{13}$/.test(cnpj)) return 'CNPJ inválido';
    return null;
  },

  email: (value) => {
    if (!value) return null;
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!regex.test(value)) return 'Email inválido';
    return null;
  },
  
  phone: (value) => {
    if (!value) return null;
    const phone = value.replace(/\D/g, '');
    if (phone.length < 10 || phone.length > 11) return 'Telefone inválido';
    return null;
  },

  currency: (value) => {
    if (!value) return null;
    if (!/[1-9]/.test(String(value).replace(/\D/g, ''))) return 'Informe um valor maior que zero';
    return null;
  },
  
  dataNascimento: (value) => {
    if (!value) return null;
    
    const data = new Date(value);
    const hoje = new Date();
    
    if (isNaN(data.getTime())) {
      return 'Data de nascimento inválida';
    }
    
    let idade = hoje.getFullYear() - data.getFullYear();
    const mes = hoje.getMonth() - data.getMonth();
    
    if (mes < 0 || (mes === 0 && hoje.getDate() < data.getDate())) {
      idade--;
    }
    
    if (idade < 18) {
      return 'É necessário ter pelo menos 18 anos';
    }
    
    if (idade > 120) {
      return 'Data de nascimento inválida';
    }
    
    return null;
  },

  // VALIDAÇÃO: Data de emissão da CNH (a partir de 01/06/2022)
  dataEmissaoCNH: (value) => {
    if (!value) return null;
    
    const dataEmissao = new Date(value);
    const dataLimite = new Date('2022-06-01');
    
    if (isNaN(dataEmissao.getTime())) {
      return 'Data de emissão inválida';
    }
    
    if (dataEmissao < dataLimite) {
      return 'A CNH deve ter sido emitida a partir de 1º de junho de 2022. Para CNHs mais antigas, utilize RG/CIN.';
    }
    
    return null;
  }
};

// Valida um campo específico
const validateField = (field, value, formData) => {
  // Validação para checkbox obrigatório
  if (field.type === 'checkbox' && field.required) {
    if (value !== true && value !== 'true') {
      return `${field.label || field.name} é obrigatório`;
    }
    return null;
  }
  
  // Validação de campo obrigatório para outros tipos (array vazio também conta
  // como vazio — usado por campos de seleção múltipla, ex: type 'multiSelect')
  const vazio = !value || value === '' || (Array.isArray(value) && value.length === 0);
  if (field.required && vazio) {
    return `${field.label || field.name} é obrigatório`;
  }

  // CEP com mais de um endereço cadastrado e nenhum escolhido ainda (ver CepField
  // em fieldComponents.js, que grava `{name}Pendente`).
  if (field.type === 'cep' && field.enderecoFields && formData?.[`${field.name}Pendente`]) {
    return 'Este CEP tem mais de um endereço. Selecione o seu na lista abaixo do CEP.';
  }

  // Radio/select com lista fixa: o valor precisa ser uma das opções. Um valor de fora
  // (ex: "Não informado" do cadastro, ou texto livre de um rascunho antigo) não aparece
  // marcado na tela, mas passaria como "preenchido" e seguiria pras regras e pra API.
  if (!vazio && (field.type === 'radio' || field.type === 'select') && Array.isArray(field.options) && field.options.length > 0) {
    const permitidos = field.options.map(opcao => String(typeof opcao === 'object' ? opcao.value : opcao));
    if (!permitidos.includes(String(value))) {
      return `Selecione uma opção válida em ${field.label || field.name}`;
    }
  }
  
  // Validações específicas por tipo
  if (value) {
    // Campo de data de nascimento: usa type 'date' como qualquer outro campo de data,
    // então a regra de idade (18-120 anos) é identificada pelo nome, não pelo tipo.
    if (field.name === 'dataNascimento') {
      const erroIdade = validations.dataNascimento(value);
      if (erroIdade) return erroIdade;
    }

    switch (field.type) {
      case 'cpf':
        return validations.cpf(value);
      case 'cnpj':
        return validations.cnpj(value);
      case 'email':
        return validations.email(value);
      case 'phone':
        return validations.phone(value);
      case 'currency':
        return validations.currency(value);
      case 'dataEmissaoCNH':
        return validations.dataEmissaoCNH(value);
      case 'number':
        if (field.integer && !Number.isInteger(Number(value))) {
          return `${field.label || field.name} deve ser um número inteiro`;
        }
        if (field.min !== undefined && Number(value) < field.min) {
          return `${field.label || field.name} deve ser no mínimo ${field.min}`;
        }
        if (field.max !== undefined && Number(value) > field.max) {
          return `${field.label || field.name} deve ser no máximo ${field.max}`;
        }
        return null;
      default:
        break;
    }
  }
  
  // Validação customizada
  if (field.validate && typeof field.validate === 'function') {
    return field.validate(value, formData);
  }
  
  return null;
};

// Única regra de visibilidade de campo do wizard — tela, validação, resumo em PDF e
// envio pra fora usam todos esta função. `showWhen` exige uma condição; `showWhenAny`
// exige pelo menos uma de uma lista (ex: declaração que aparece se a certidão
// Estadual OU a Federal deu positiva).
export const isCampoVisivel = (field, formData = {}) => {
  if (field.showWhen && formData[field.showWhen.field] !== field.showWhen.value) return false;
  if (field.showWhenAny && !field.showWhenAny.some(condicao => formData[condicao.field] === condicao.value)) return false;
  return true;
};

// O form não limpa um campo quando ele some por `showWhen`: o valor digitado antes
// continua no formData (ex: endereço profissional preenchido antes de marcar "Não",
// Órgão/Cargo público antes de trocar pra "Não exerce"). Quem envia dados pra fora
// (DadosRequerimento, PUT do cadastro) usa isto pra mandar só o que está visível.
export const removerCamposOcultos = (formData = {}, steps = []) => {
  const visiveis = { ...formData };
  steps.forEach(step => step.sections.forEach(section => section.fields.forEach(field => {
    if (!isCampoVisivel(field, formData)) {
      delete visiveis[field.name];
    }
  })));
  return visiveis;
};

// Valida todos os campos de uma seção
export const validateSection = (section, formData) => {
  const errors = {};
  
  section.fields.forEach(field => {
    // Verifica se o campo deve ser mostrado (condicional)
    if (!isCampoVisivel(field, formData)) {
      return;
    }
    
    const error = validateField(field, formData[field.name], formData);
    if (error) {
      errors[field.name] = error;
    }
  });
  
  return errors;
};
